import { useEffect, useState } from "react";
import { Button, Modal, Select, message } from "antd";
import { UserCog } from "lucide-react";
import { useAccount } from "@vojomo-core/shared";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

type ActAsUser = { id: string; name?: string; email?: string; company?: string };

/**
 * AdminActAsBar — platform-admin "Act as user" tool (dogfooding / support).
 *
 * When NOT acting as anyone and the real user is an admin: a small floating
 * "Act as user" control (pick a user → become them). When acting as someone
 * (the /auth response carries actingAsAdminId): a persistent banner with a Stop
 * button. Backed by the admin-gated /admin/act_as endpoints; the real admin token
 * still gates them, so Stop always works.
 */
export function AdminActAsBar() {
  const { vojomoAccount } = useAccount();
  const acct = vojomoAccount as unknown as {
    name?: string; email?: string;
    is_admin?: boolean; is_super_admin?: boolean;
    actingAsAdminId?: string; actingAsAdminName?: string;
  } | null;

  const actingAs = acct?.actingAsAdminId;
  const isAdmin = Boolean(acct?.is_admin || acct?.is_super_admin);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [users, setUsers] = useState<ActAsUser[]>([]);
  const [selected, setSelected] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pickerOpen || users.length) return;
    (async () => {
      try {
        const r = await fetch(`${SERVER_URL}/admin/act_as/users`, { credentials: "include" });
        if (r.ok) setUsers(await r.json());
      } catch { /* ignore */ }
    })();
  }, [pickerOpen, users.length]);

  const startActAs = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await fetch(`${SERVER_URL}/admin/act_as`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: selected }),
      });
      if (r.ok) {
        // Land on the acted-as user's OWN roadmaps list, never the current URL —
        // which they may not have access to (that showed "Session not found").
        window.location.href = "/roadmap/myroadmaps";
      } else {
        message.error("Couldn't act as that user.");
        setBusy(false);
      }
    } catch { message.error("Couldn't act as that user."); setBusy(false); }
  };

  const stopActAs = async () => {
    setBusy(true);
    try {
      await fetch(`${SERVER_URL}/admin/act_as/clear`, { method: "POST", credentials: "include" });
    } catch { /* ignore */ }
    window.location.href = "/roadmap/myroadmaps";
  };

  if (actingAs) {
    return (
      <div
        style={{
          position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
          zIndex: 3000, display: "flex", alignItems: "center", gap: 12,
          background: "#2c1561", color: "#fff", borderRadius: 999,
          padding: "8px 14px", boxShadow: "0 4px 16px rgba(0,0,0,0.25)", fontSize: 13,
        }}
      >
        <UserCog style={{ width: 15, height: 15 }} />
        <span>
          Acting as <b>{acct?.name || acct?.email}</b>
          {acct?.actingAsAdminName ? ` — you're ${acct.actingAsAdminName}` : ""}
        </span>
        <Button size="small" type="primary" ghost loading={busy} onClick={stopActAs}
          style={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}>
          Stop
        </Button>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <>
      <button
        onClick={() => setPickerOpen(true)}
        title="Admin: act as another user"
        style={{
          position: "fixed", bottom: 16, left: 16, zIndex: 3000,
          display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
          background: "#fff", color: "#2c1561", border: "1px solid #e6e3ef",
          borderRadius: 999, padding: "7px 12px", fontSize: 12.5, fontWeight: 600,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        }}
      >
        <UserCog style={{ width: 14, height: 14 }} /> Act as user
      </button>
      <Modal
        open={pickerOpen}
        onCancel={() => setPickerOpen(false)}
        onOk={startActAs}
        okText="Act as user"
        okButtonProps={{ disabled: !selected, loading: busy }}
        title={<span style={{ display: "flex", alignItems: "center", gap: 8 }}><UserCog style={{ width: 16, height: 16 }} /> Act as another user</span>}
        width={460}
      >
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
          Admin tool: view the app as another user (e.g. a contributor on your roadmap) to test the
          multi-user flow. A banner will show while you're acting-as; click Stop to return to yourself.
        </p>
        <Select
          showSearch
          style={{ width: "100%" }}
          placeholder="Search by name or email…"
          value={selected}
          onChange={setSelected}
          optionFilterProp="label"
          options={users
            .filter((u) => u.email || u.name)
            .map((u) => ({
              value: u.id,
              label: `${u.name || "(no name)"} — ${u.email || "?"}${u.company ? ` · ${u.company}` : ""}`,
            }))}
        />
      </Modal>
    </>
  );
}
